import { FC, useContext, useState } from 'react';

import {
  BonusIcon,
  BottomIcon,
  CloseIcon,
  Label,
  Paragraph,
  Separator,
  TopIcon,
} from '@smartshell/ui-kit';
import day from 'dayjs';

import { AuthContext } from 'auth';
import { LocalizationContext } from 'localization';
import { SettingsContext } from 'settings';
import { EPaymentItemType, EPaymentMethod } from 'types';
import { formatSumPayment, getTime } from 'utils';

import { ISidebarProps } from './interfaces';
import s from './Sidebar.module.scss';
import { getItemTitle, getRefundStatusComponent } from './utils';

export const Sidebar: FC<ISidebarProps> = ({
  payment,
  openPostPaymentModal,
  setSelectedPaymentId,
  size,
}) => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: {
        isRefundEnabled,
        refundHeadroom,
        currency: { sign: currency },
      },
    },
  } = useContext(SettingsContext);

  const {
    data: { permissions },
  } = useContext(AuthContext);

  if (!payment) {
    return (
      <div className={s.container}>
        <div className={s['placeholder']}>
          {t('payment.sidebar.placeholder')}
        </div>
      </div>
    );
  }

  const {
    id,
    created_at,
    items,
    cash_sum,
    card_sum,
    method,
    sum,
    client_session,
    not_refundable,
    bonus,
    sums,
  } = payment;
  const createdAt = day(created_at);

  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={s.container}>
      {size <= 1864 && (
        <div onClick={() => setSelectedPaymentId(null)} className={s.close}>
          <CloseIcon size="l" />
        </div>
      )}
      <div className={s.info}>
        <span>{t('payment.sidebar.number', id)}</span>
        <span className={s.time}>{createdAt.format('D MMM YYYY HH:mm')}</span>
      </div>
      {getRefundStatusComponent(
        payment,
        isRefundEnabled,
        refundHeadroom,
        t,
        permissions,
        openPostPaymentModal,
        not_refundable
      )}
      <div className={s.receipt}>
        <span className={s.title}>{t('payment.receipt')}</span>
        <div className={s.items}>
          {items.map((item, index) => (
            <div
              key={`payment-${id}-receipt-item-${item.id}-${index}`}
              className={s.item}
            >
              <div className={s.entity}>
                {getItemTitle(item, t)}
                {!['DEPOSIT', 'BONUS'].includes(item.type) && (
                  <span className={s.price}>
                    {t(
                      'payment.sold',
                      item.amount,
                      formatSumPayment(item.sum / item.amount),
                      currency
                    )}
                  </span>
                )}
              </div>

              {item?.type === EPaymentItemType.BONUS ? (
                <div className={s.bonusValue}>
                  <BonusIcon size="s" /> <span>{item.sum}</span>
                </div>
              ) : (
                <span className={s.sum}>
                  {formatSumPayment(item.sum)} {currency}
                </span>
              )}
            </div>
          ))}
        </div>
        {method === EPaymentMethod.POSTPAID && client_session.hosts && (
          <>
            <div className={s.hosts__wrapper}>
              <div
                className={s.hosts__header}
                onClick={() => setIsOpen((prev) => !prev)}
              >
                <Paragraph color="secondary" size="s">
                  {t('payment.sidebar.hosts.title')}
                </Paragraph>

                {isOpen ? (
                  <TopIcon size="s" color="secondary" />
                ) : (
                  <BottomIcon size="s" color="secondary" />
                )}
              </div>
              {isOpen && (
                <div className={s.hosts}>
                  {client_session.hosts.map(
                    ({ host: { alias }, started_at, finished_at }) => {
                      const start = day(started_at);
                      const finish = day(finished_at);

                      return (
                        <div key={alias} className={s.host}>
                          <div>
                            <Paragraph color="secondary" size="s">
                              {alias}
                            </Paragraph>
                          </div>
                          <Paragraph color="secondary" size="s">
                            {getTime(t, finish.diff(start, 'minutes', false))}
                          </Paragraph>
                        </div>
                      );
                    }
                  )}
                </div>
              )}
            </div>
            <Separator />
          </>
        )}
        {items[0]?.type !== EPaymentItemType.BONUS && (
          <div className={s.totalSums}>
            <div className={s.sum}>
              {t('total')}
              <span>
                {items[0].type === EPaymentItemType.DEPOSIT
                  ? sums?.deposit + ' ' + currency
                  : sum + '' + currency}
              </span>
            </div>
            {method === EPaymentMethod.COMPOSITE && (
              <div className={s.composite}>
                <div className={s.sum}>
                  {t('money.card')}
                  <span>
                    {card_sum} {currency}
                  </span>
                </div>
                <div className={s.sum}>
                  {t('money.cash')}
                  <span>
                    {cash_sum} {currency}
                  </span>
                </div>
              </div>
            )}
            {Boolean(bonus) && (
              <div className={s.bonus}>
                <Paragraph color="secondary">{t('payment.bonuses')}</Paragraph>
                <Label
                  iconName={
                    items?.some((i) => i.type === EPaymentItemType.DEPOSIT)
                      ? 'BonusAddIcon'
                      : 'BonusRemoveIcon'
                  }
                  size="s"
                  color={
                    items?.some((i) => i.type === EPaymentItemType.DEPOSIT)
                      ? 'green'
                      : 'red'
                  }
                  text={String(bonus)}
                />
              </div>
            )}
            {Boolean(sums?.promo_code) && (
              <div className={s.bonus}>
                <Paragraph color="secondary">
                  {t('payments.promocode')}
                </Paragraph>
                <span className={s.value}>{sums?.promo_code}</span>
              </div>
            )}
            {Boolean(sums?.cashback) && (
              <div className={s.bonus}>
                <Paragraph color="secondary">
                  {t('payments.cashback')}
                </Paragraph>
                <span className={s.value}>{sums?.cashback}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
